"use client"
import styled from "styled-components"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
// Corrected the import: Replaced FiRocket with FiTrendingUp
import { FiAward, FiMapPin, FiCode, FiHeart, FiBriefcase, FiBookOpen, FiTrendingUp } from "react-icons/fi"

// --- STYLED COMPONENTS ---

const AboutSection = styled.section`
  padding: 6rem 0;
  background: transparent;
  position: relative;
  overflow: hidden;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #2A9D8F, #E9C46A);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem;
  align-items: flex-start;
  margin-bottom: 5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const TimelineContainer = styled(motion.div)`
  position: relative;
  padding-left: 2.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.5rem;
    bottom: 0.5rem;
    width: 2px;
    background: rgba(38, 70, 83, 0.2);
  }
`

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const TimelineIcon = styled.div`
  position: absolute;
  left: -2.5rem;
  top: 0;
  transform: translateX(-50%);
  width: 50px;
  height: 50px;
  background: #F4F1DE;
  border: 2px solid #2A9D8F;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #264653;
  box-shadow: 0 0 15px rgba(42, 157, 143, 0.3);
`

const TimelineContent = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1B1B1E;
    margin-bottom: 0.5rem;
  }
  p {
    color: #1B1B1E;
    opacity: 0.85;
    line-height: 1.7;
  }
`

const ImageContainer = styled(motion.div)`
  padding: 1rem;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(38, 70, 83, 0.1);
  box-shadow: 0 10px 30px rgba(38, 70, 83, 0.1);

  @media (max-width: 992px) {
    display: none;
  }
`

const ImagePlaceholder = styled.div`
  width: 100%;
  padding-bottom: 100%;
  border-radius: 1rem;
  background: linear-gradient(135deg, #264653, #2A9D8F);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  color: #F4F1DE;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
`

const KeyFactsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`

const FactCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  padding: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  .icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $color }) => $color}1A;
    color: ${({ $color }) => $color};
    margin-bottom: 1rem;
    transition: all 0.3s ease;
  }
  
  h4 {
    font-weight: 600;
    color: #1B1B1E;
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.95rem;
    color: #1B1B1E;
    opacity: 0.8;
    line-height: 1.6;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
    border-color: ${({ $color }) => $color};
    
    .icon-wrapper {
      transform: scale(1.1) rotate(5deg);
      background: ${({ $color }) => $color};
      color: #FFFFFF;
    }
  }
`

// --- DATA ---

const journeySteps = [
  {
    icon: <FiBookOpen size={24} />,
    title: "Academic Foundation",
    description: "Started my journey pursuing a Bachelor's in Computer and Software Engineering at the University of Rwanda, building a strong theoretical foundation.",
  },
  {
    icon: <FiBriefcase size={24} />,
    title: "First Professional Steps",
    description: "Gained practical experience at the SheCanCode program, developing key skills in database management, API development, and software testing.",
  },
  {
    icon: <FiAward size={24} />,
    title: "Full Stack Certification",
    description: "Completed an intensive 4-month Full Stack Development program at Klab, mastering modern web technologies like React, Node.js, and best practices.",
  },
  {
    // Corrected the icon: Replaced FiRocket with FiTrendingUp
    icon: <FiTrendingUp size={24} />,
    title: "Expanding Horizons",
    description: "Currently expanding my expertise into mobile development and exploring cloud technologies, always eager to learn and apply cutting-edge solutions.",
  },
]

// Note: I've added a complementary orange color from a popular palette that works well with the existing colors.
const keyFacts = [
  { icon: <FiCode size={24}/>, title: 'Specialization', description: 'Web & Mobile Development, E-commerce Solutions', color: '#264653' },
  { icon: <FiMapPin size={24}/>, title: 'Location', description: 'Kigali, Rwanda. Open to remote opportunities.', color: '#2A9D8F' },
  { icon: <FiHeart size={24}/>, title: 'Interests', description: 'Traveling, Reading, Music, and collaborative coding.', color: '#E9C46A' },
  { icon: <FiAward size={24}/>, title: 'Certification', description: 'Certified Full Stack Developer from Klab.', color: '#E76F51' },
]


// --- MAIN COMPONENT ---

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const fadeInUp = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  }

  return (
    <AboutSection id="about" ref={ref}>
      <Container>
        <SectionHeader variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <h2>
            My <span className="highlight">Journey</span> So Far
          </h2>
          <p>A blend of academic rigor and hands-on experience, driven by a passion for building things that make a difference.</p>
        </SectionHeader>

        <AboutGrid>
          <TimelineContainer variants={staggerContainer} initial="hidden" animate={inView ? "visible" : "hidden"}>
            {journeySteps.map((step, index) => (
              <TimelineItem key={index} variants={fadeInUp}>
                <TimelineIcon>{step.icon}</TimelineIcon>
                <TimelineContent>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineContainer>
          
          <ImageContainer variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
              <img src="/about.jpg" alt="About Me" style={{ width: '100%', height: 'auto', borderRadius: '1rem' }} />
          </ImageContainer>
        </AboutGrid>
        
        <KeyFactsGrid variants={staggerContainer} initial="hidden" animate={inView ? "visible" : "hidden"}>
          {keyFacts.map((fact, index) => (
            <FactCard key={index} variants={fadeInUp} $color={fact.color}>
                <div className="icon-wrapper">{fact.icon}</div>
                <h4>{fact.title}</h4>
                <p>{fact.description}</p>
            </FactCard>
          ))}
        </KeyFactsGrid>

      </Container>
    </AboutSection>
  )
}

export default About